import { LucideIcon } from 'lucide-react'

import { LocationType } from '~/client/graph'
import {
  getGlyph,
  getPictogramGlyph,
} from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import locationTypeToTagIconMap from '~/client/src/shared/constants/locationTypeToTagIconMap'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import type { PictogramIconName } from '~/client/src/shared/icons/pictograms'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  createdLocationKind,
  type ICreatableLocationKinds,
} from '~/client/src/shared/resources/domain/creatableLocationKinds'
import { INFORMATION_POINT_KINDS } from '~/client/src/shared/resources/domain/informationPointGroups'
import {
  GENERAL_LEAF_BY_ROOT,
  iconNameFromKind,
  LocationKind,
  type LocationKindValue,
} from '~/client/src/shared/resources/domain/locationKind'

export interface ILocationListObject {
  getLabel?: () => string
  dataObjectType?: LocationType
  iconName?: MapViewLocationIcon | PictogramIconName
  /**
   * The kind this entry creates, when its icon cannot say. The Vertical Object root shares a
   * glyph with its Stairway child, and an Information Point is drawn with a library icon that
   * names no kind. Every other entry is named by its icon.
   */
  kind?: LocationKindValue
  /** Names the created location after its icon rather than its type. */
  isNamedByIcon?: boolean
  sitemapItemType?: MapViewItemType
  children?: ILocationListObject[]
  /** Places an Information Point of any type from InformationPointPicker. */
  opensInformationPointPicker?: boolean
  /** Shown but not pickable: the project's Type list is not known yet, or only a child can be created. */
  isDisabled?: boolean
}

// Drawn with the icon its kind defaults to, so a new Information Point already carries the sign
// it is created with.
const informationPoint = (
  kind: LocationKindValue,
  getLabel: () => string,
): ILocationListObject => ({
  dataObjectType: LocationType.LogisticsObject,
  iconName: iconNameFromKind(kind),
  kind,
  getLabel,
})

/** The Information Point types, for a menu that lists them instead of opening InformationPointPicker. */
export const informationPointTypeObjects: ILocationListObject[] =
  INFORMATION_POINT_KINDS.filter(
    kind => kind !== GENERAL_LEAF_BY_ROOT[LocationKind.InformationPoints],
  ).map(kind =>
    informationPoint(
      kind,
      () => Localization.translator.informationPointTypes[kind],
    ),
  )

const textSticker = 'Text sticker'
const drawingSticker = 'Drawing sticker'

export const locationObjects: ILocationListObject[][] = [
  [
    {
      dataObjectType: LocationType.Building,
      getLabel: () => Localization.translator.building,
    },
    {
      dataObjectType: LocationType.Zone,
      getLabel: () => Localization.translator.zone,
    },
    {
      dataObjectType: LocationType.Staging,
      getLabel: () => Localization.translator.staging,
    },
    {
      dataObjectType: LocationType.Area,
      getLabel: () => Localization.translator.area,
    },
  ],
  [
    {
      dataObjectType: LocationType.VerticalObject,
      iconName: MapViewLocationIcon.Stairs,
      // Shares the stairs glyph with its own Stairway child, so the kind is carried here
      // rather than derived from the icon.
      kind: GENERAL_LEAF_BY_ROOT[LocationKind.VerticalObject],
      getLabel: () => Localization.translator.verticalObject,
      children: [
        {
          dataObjectType: LocationType.VerticalObject,
          iconName: MapViewLocationIcon.Stairs,
          getLabel: () => Localization.translator.stairway,
        },
        {
          dataObjectType: LocationType.VerticalObject,
          iconName: MapViewLocationIcon.Elevator,
          getLabel: () => Localization.translator.elevator,
        },
        {
          dataObjectType: LocationType.VerticalObject,
          iconName: MapViewLocationIcon.Shaft,
          getLabel: () => Localization.translator.shaft,
        },
      ],
    },
  ],
  [
    {
      dataObjectType: LocationType.Route,
      getLabel: () => Localization.translator.route,
    },
    {
      dataObjectType: LocationType.Gate,
      getLabel: () => Localization.translator.gate,
    },
    {
      dataObjectType: LocationType.InteriorPath,
      getLabel: () => Localization.translator.interiorPath,
    },
    {
      dataObjectType: LocationType.InteriorDoor,
      getLabel: () => Localization.translator.interiorDoor,
    },
  ],
  [
    {
      dataObjectType: LocationType.OffloadingEquipment,
      iconName: MapViewLocationIcon.Equipment,
      getLabel: () => Localization.translator.sharedResources,
      children: [
        {
          dataObjectType: LocationType.OffloadingEquipment,
          iconName: MapViewLocationIcon.Crane,
          getLabel: () => Localization.translator.crane,
        },
        {
          dataObjectType: LocationType.OffloadingEquipment,
          iconName: MapViewLocationIcon.Hoist,
          getLabel: () => Localization.translator.hoist,
        },
        {
          dataObjectType: LocationType.OffloadingEquipment,
          iconName: MapViewLocationIcon.AerialLift,
          getLabel: () => Localization.translator.aerialLift,
        },
        {
          dataObjectType: LocationType.OffloadingEquipment,
          iconName: MapViewLocationIcon.Gradall,
          getLabel: () => Localization.translator.gradall,
        },
        {
          dataObjectType: LocationType.OffloadingEquipment,
          iconName: MapViewLocationIcon.ForkLift,
          getLabel: () => Localization.translator.forkLift,
        },
      ],
    },
    {
      ...informationPoint(
        GENERAL_LEAF_BY_ROOT[LocationKind.InformationPoints],
        () => Localization.translator.informationPoints,
      ),
      opensInformationPointPicker: true,
    },
  ],
  [
    {
      iconName: MapViewLocationIcon.TextBox,
      sitemapItemType: MapViewItemType.TextBox,
      getLabel: () => textSticker,
    },
    {
      iconName: MapViewLocationIcon.Line,
      sitemapItemType: MapViewItemType.Line,
      getLabel: () => drawingSticker,
    },
  ],
]

// iconName is the more specific of the two (a VerticalObject is an elevator, stairs or a
// shaft), so it wins where an entry carries both.
export function getLocationObjectIcon(object: ILocationListObject): LucideIcon {
  if (object.iconName) {
    return (getGlyph(object.iconName) ?? getPictogramGlyph(object.iconName))
      .Icon
  }
  return locationTypeToTagIconMap[object.dataObjectType]
}

/** The kind an entry creates; null for one that creates no location. */
export const locationObjectKind = (
  object: ILocationListObject,
): string | null =>
  createdLocationKind(object.kind, object.iconName, object.dataObjectType)

/** Whether an entry creates what the project can create. One that creates no location always does. */
const canCreateLocationObject = (
  object: ILocationListObject,
  isCreatable: ICreatableLocationKinds['isCreatable'],
): boolean => {
  const kind = locationObjectKind(object)
  return !kind || isCreatable(kind)
}

/** Identifies an entry among its siblings, whichever of them the project can create. */
export const locationObjectKey = (object: ILocationListObject): string =>
  `${object.dataObjectType}-${object.iconName ?? ''}-${object.kind ?? ''}`

/**
 * An entry with only the children the project can create, disabled when it cannot create what
 * it does itself; null when it creates nothing the project can.
 */
export const creatableLocationObject = (
  object: ILocationListObject,
  isCreatable: ICreatableLocationKinds['isCreatable'],
  canCreate: boolean = canCreateLocationObject(object, isCreatable),
): ILocationListObject | null => {
  const children = object.children?.filter(child =>
    canCreateLocationObject(child, isCreatable),
  )

  return canCreate || children?.length
    ? {
        ...object,
        children: children?.length ? children : undefined,
        isDisabled: !canCreate,
      }
    : null
}

const paletteObject = (
  object: ILocationListObject,
  isCreatable: ICreatableLocationKinds['isCreatable'],
): ILocationListObject | null =>
  creatableLocationObject(
    object,
    isCreatable,
    object.opensInformationPointPicker
      ? INFORMATION_POINT_KINDS.some(isCreatable)
      : canCreateLocationObject(object, isCreatable),
  )

const disabledUntilReady = (
  object: ILocationListObject,
): ILocationListObject => ({
  ...object,
  children: object.children?.map(disabledUntilReady),
  isDisabled: !!locationObjectKind(object),
})

/**
 * The palette a project can use: an entry is dropped when what it creates is not creatable, a
 * flyout once nothing in it is, and Information Points once no type can be placed. Until the
 * Type list is known every entry stays, those that create a location disabled, so none jumps in.
 */
export function paletteLocationObjects({
  isReady,
  isCreatable,
}: ICreatableLocationKinds): ILocationListObject[][] {
  if (!isReady) {
    return locationObjects.map(section => section.map(disabledUntilReady))
  }

  return locationObjects
    .map(section =>
      section.map(object => paletteObject(object, isCreatable)).filter(Boolean),
    )
    .filter(section => section.length)
}
